/**
 * Shared, reference-counted background-scroll lock (2026-08-27).
 *
 * Every full-bleed workspace (Pulse/Team, CRM, Billing, Roadmap, Schedule Intel, Studio,
 * Templates) independently set `document.body.style.overflow = 'hidden'` on mount and restored
 * `''` on unmount — the fix for "the outline's scrollbar shows behind the workspace." That works
 * in isolation, but the pattern doesn't compose: if a SECOND thing that also locks scroll opens
 * on top of an already-open workspace (a modal launched from inside it, say), the naive version
 * unconditionally restores `''` on its own unmount — even though the workspace underneath is
 * still open and still needs the lock. The outline becomes scrollable again the moment the
 * modal on top closes, despite the workspace still being on screen. Matt: "I can still see the
 * outline scrolls behind these tools" — this is the mechanism that bug looks like.
 *
 * A plain module-level counter fixes it: the DOM is only ever unlocked when the LAST locker lets
 * go, however many are stacked. Locks both `<body>` and `<html>` — most browsers apply `<body>`'s
 * overflow to the viewport when `<html>` has none set of its own, but not all configurations do,
 * and locking both costs nothing.
 */

import { useEffect } from 'react'

let lockCount = 0
let savedBodyOverflow = null
let savedHtmlOverflow = null

/** Take one lock. Returns a matching release function — call it exactly once. */
export function lockScroll() {
  if (lockCount === 0) {
    savedBodyOverflow = document.body.style.overflow
    savedHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
  }
  lockCount++
  let released = false
  return () => {
    if (released) return   // guard against a double-release if a caller invokes it twice
    released = true
    lockCount = Math.max(0, lockCount - 1)
    if (lockCount === 0) {
      document.body.style.overflow = savedBodyOverflow || ''
      document.documentElement.style.overflow = savedHtmlOverflow || ''
    }
  }
}

/**
 * Lock background scroll while `active` is true. Drop-in replacement for the
 * `useEffect(() => { if (!active) return; document.body.style.overflow = 'hidden'; return () =>
 * ... }, [active])` pattern repeated across the workspaces — same call shape, but composes
 * correctly with any other lock (workspace, modal, or another workspace) held at the same time.
 */
export function useScrollLock(active) {
  useEffect(() => {
    if (!active) return
    return lockScroll()
  }, [active])
}
